import React, { useEffect, useMemo, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { Autocomplete, Button, Checkbox, FormControlLabel, Paper, Stack, TextField } from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { HorizontalNavBar } from "components/HorizontalNavBar/HorizontalNavBar";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useModals } from "hooks";
import { IApiServiceError, useAccountSelector, useDealActions } from "store";
import { Characteristic, CharacteristicsTableItem } from "store/types";

import { Footer } from "./Footer";
import { GlobalTab } from "./GlobalTab";
import { ListTab } from "./ListTab";

const baseCategoryOptions = [
  { id: 0, name: "General" },
  { id: 1, name: "Legal" },
  { id: 3, name: "Financial" }
];

const baseDataTypeOption = [
  { id: 0, name: "currency" },
  { id: 1, name: "number" },
  { id: 2, name: "date" },
  { id: 3, name: "text" },
  { id: 4, name: "percentage" },
  { id: 5, name: "list" }
];

const ASSET_REPORT = "Asset Report";
const CURRENCY_DATA_TYPE = "currency";

const isCurrencyName = (name?: string | null) => name?.trim().toLowerCase() === CURRENCY_DATA_TYPE;

interface Props {
  initialState?: CharacteristicsTableItem | null;
  onSubmit?: (values: CharacteristicsTableItem) => Promise<Characteristic>;
  onCancel: () => void;
  isLoading: boolean;
}

export interface ListOption {
  id: string;
  value: string;
  subcategories: number[];
}

export type Values = Omit<CharacteristicsTableItem, "list_options"> & {
  list_options: Array<ListOption>;
};

export type PlainDataFields = keyof Omit<CharacteristicsTableItem, "list_options">;

export const Form: React.FC<Props> = ({ initialState, onSubmit, isLoading, onCancel }) => {
  const { single_asset_report } = useAccountSelector() || {};
  const { getSubCategoriesAction, createSubCategoryAction, getDealsMissingCharacteristicAction } = useDealActions();
  const { createModal, infoModal } = useModals();

  const [selectedTab, setSelectedTab] = useState<"global" | "list">("global");
  const [values, setValues] = useState<Values>(transformInitialStateToValues(initialState));
  const [errors, setErrors] = useState<Partial<Record<keyof Values, string[]>>>({});
  const [optionsErrors, setOptionsErrors] = useState<Record<string, string>>({});

  const categoryOption = useMemo(() => {
    const options = [...baseCategoryOptions];
    if (single_asset_report) {
      options.push({ id: 4, name: ASSET_REPORT });
    }
    return options;
  }, [single_asset_report]);

  const {
    action: getSubCategories,
    data: subCategories = [],
    loading: getSubCategoriesLoading,
    setData: setSubCategories
  } = useActionHook(getSubCategoriesAction, {});

  const handleTabChange = (tab: "global" | "list") => {
    setSelectedTab(tab);
  };

  const clearError = (field: keyof Values) => {
    if (!errors[field]) return;

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const updatePlainData = (field: PlainDataFields, value: CharacteristicsTableItem[PlainDataFields]) => {
    clearError(field);
    setValues((prev) => {
      const next = { ...prev, [field]: value };

      // Force data_type to "currency" when the characteristic name is "currency"
      if (field === "name" && isCurrencyName(value as string)) {
        clearError("data_type");
        next.data_type = CURRENCY_DATA_TYPE;
      }

      return next;
    });

    // Auto-switch to list tab when data_type is changed to "list"
    if (field === "data_type" && value !== "list" && selectedTab === "list") {
      setSelectedTab("global");
    }
  };

  const handleCreateNewSubcategory = () => {
    createModal({
      action: async (title: string) => {
        const newSubcategory = await createSubCategoryAction(title);
        setSubCategories((prev = []) => [...prev, newSubcategory]);

        updatePlainData("subcategory_ids", [...(values?.subcategory_ids || []), newSubcategory.id]);
      },
      title: "Create new sub category",
      submitButtonTitle: "Create",
      label: "Sub category name"
    });
  };

  const handleSubmit = async (values: Values) => {
    const { list_options, required_to_status_ids, ...rest } = values;

    const isFormValid = validateForm(values);
    const isOptionsListValid = validateListOptions(list_options);

    if (!isFormValid || !isOptionsListValid) return;

    // Remove read-only fields that shouldn't be sent
    const readOnlyFields = ["created_at", "updated_at", "subcategory_name"];
    const cleanRest = Object.fromEntries(Object.entries(rest).filter(([key]) => !readOnlyFields.includes(key))) as Omit<
      typeof rest,
      "created_at" | "updated_at" | "subcategory_name"
    >;

    const dataToSend: CharacteristicsTableItem = {
      ...cleanRest,
      ...(cleanRest.category_name !== ASSET_REPORT && { subcategory_ids: [] }),
      list_options: [],
      required_to_status_ids
    };
    // Filter out empty options
    const filteredListOptions = list_options.filter((option) => option.value);

    // Transform list options to the format that the backend expects
    // Only Asset Report can have sub categories
    if (rest.category_name === ASSET_REPORT) {
      const listOptionsToSet: Record<number, string[]> = {};

      filteredListOptions.forEach(({ subcategories, value }) => {
        subcategories.forEach((subcategoryId) => {
          if (!listOptionsToSet[subcategoryId]) {
            return (listOptionsToSet[subcategoryId] = [value]);
          }
          listOptionsToSet[subcategoryId].push(value);
        });
      });
      dataToSend.list_options = listOptionsToSet;
    } else {
      dataToSend.list_options = filteredListOptions.map(({ value }) => value);
    }

    try {
      // Save the characteristic first
      const savedCharacteristic = await onSubmit?.(dataToSend);

      // After saving, check for missing deals if required_to_status_ids is set
      if (savedCharacteristic && !!dataToSend.required_to_status_ids?.length) {
        try {
          const { deals } = await getDealsMissingCharacteristicAction({
            characteristicId: savedCharacteristic.id
          });

          if (deals.length) {
            const dealList = deals.map((deal) => `• ${deal.title} (${deal.status_name})`).join("\n");
            const title = `The following ${deals.length} deal${deals.length > 1 ? "s are" : " is"} missing this field:`;
            infoModal({
              title,
              msg: dealList,
              footerMsg: "Please update these deals to ensure data completeness.",
              buttonText: "Close"
            });
          }
        } catch (error) {
          // If check fails, don't block the user - save was successful
          console.error("Failed to check missing deals:", error);
        }
      }
    } catch (error) {
      const formErrors = (error as IApiServiceError)?.errors;
      if (!formErrors) return;
      setErrors(formErrors);
    }
  };

  const validateForm = (values: Values): boolean => {
    const errors: Partial<Record<keyof Values, string[]>> = {};

    if (!values.name) {
      errors.name = ["The name field is required."];
    }

    if (!values.category_name) {
      errors.category_name = ["Category is required."];
    }

    if (values.category_name === ASSET_REPORT && !values.subcategory_ids.length) {
      errors.subcategory_ids = ["Please select at least one subcategory."];
    }

    if (values.data_type === "list" && !values.list_options.length) {
      errors.list_options = ["At least one option is required when data type is list."];
    }

    setErrors(errors);
    return !Object.keys(errors).length;
  };

  const validateListOptions = (optionsList: ListOption[]): boolean => {
    const errors: Record<string, string> = {};
    const requireSubcategories = values.category_name === ASSET_REPORT;

    optionsList.forEach((option) => {
      if (!option.value) {
        errors[`${option.id}-value`] = "The value field is required.";
      }
      if (requireSubcategories && !option.subcategories.length) {
        errors[`${option.id}-subcategories`] = "At least one subcategory is required.";
      }
    });

    setOptionsErrors(errors);
    return !Object.keys(errors).length;
  };

  useEffect(() => {
    if (!single_asset_report) return;
    getSubCategories();
  }, []);

  // Listen to "list changes" - at least one option is required
  useEffect(() => {
    if (!errors.list_options) return;

    if (values.data_type !== "list") {
      clearError("list_options");
    }

    if (values.list_options.length) {
      clearError("list_options");
    }
  }, [values]);

  return (
    <ProgressOverlay
      containerSX={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}
      inProgress={isLoading || getSubCategoriesLoading}
    >
      <Stack sx={{ p: "30px", gap: "24px", flex: 1, overflow: "auto", alignItems: "stretch", minHeight: 0 }}>
        <TextField
          error={!!errors.name}
          helperText={errors.name}
          variant="outlined"
          size="small"
          label="Name"
          onChange={(e) => updatePlainData("name", e.target.value)}
          value={values.name}
          disabled={values.is_locked_to_edit}
        />
        <Autocomplete
          getOptionLabel={(option: { id: number; name: string }) => option.name}
          size="small"
          options={categoryOption}
          value={getAutocompleteValue(values?.category_name, categoryOption)}
          disableClearable
          onChange={(_, value) => updatePlainData("category_name", value?.name)}
          disabled={values.is_locked_to_edit}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!errors.category_name}
              helperText={errors.category_name}
              variant="outlined"
              size="small"
              label="Category"
              value={values?.category_name}
              disabled={values.is_locked_to_edit}
            />
          )}
        />
        {values?.category_name === "Asset Report" && (
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            multiple
            size="small"
            options={subCategories}
            disableClearable
            disableCloseOnSelect
            value={subCategories.filter((option) => values?.subcategory_ids?.includes(option.id))}
            disabled={values.is_locked_to_edit || values.data_type === "list"}
            onChange={(_, value) => {
              updatePlainData(
                "subcategory_ids",
                value.map((v) => v.id)
              );
            }}
            slots={{
              paper: ({ children, ...props }: React.ComponentProps<typeof Paper>) => (
                <Paper
                  sx={{
                    position: "absolute",
                    width: "305px",
                    backgroundColor: "white",
                    zIndex: "10",
                    borderRadius: "10px",
                    "& .MuiAutocomplete-listbox": {
                      "& .MuiAutocomplete-option[aria-disabled='true']": {
                        opacity: 1,
                        "&.Mui-focused": {
                          opacity: 1
                        }
                      },
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        bgcolor: "#edf1f9",
                        "&.Mui-focused": {
                          bgcolor: "#edf1f9"
                        }
                      }
                    },
                    "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                      bgcolor: "#edf1f9"
                    }
                  }}
                  {...props}
                >
                  <>
                    {children}
                    <Button
                      onMouseDown={handleCreateNewSubcategory}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      Create new
                    </Button>
                  </>
                </Paper>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!errors.subcategory_ids}
                helperText={errors.subcategory_ids}
                variant="outlined"
                size="small"
                label="Sub Category"
              />
            )}
          />
        )}
        <Autocomplete
          getOptionLabel={(option: { id: number; name: string }) =>
            option.name.charAt(0).toUpperCase() + option.name.slice(1)
          }
          size="small"
          options={baseDataTypeOption}
          value={getAutocompleteValue(values?.data_type, baseDataTypeOption)}
          disableClearable
          onChange={(_, value) => updatePlainData("data_type", value?.name)}
          disabled={values.is_locked_to_edit || isCurrencyName(values?.name)}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!errors.data_type || !!errors.list_options}
              helperText={errors.data_type || errors.list_options}
              variant="outlined"
              size="small"
              label="Data Type"
              value={values?.data_type}
              disabled={values.is_locked_to_edit || isCurrencyName(values?.name)}
            />
          )}
        />
        <RoleComponentGuard availableRoles={["admin"]}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.is_locked_to_edit}
                onChange={(e) => updatePlainData("is_locked_to_edit", e.target.checked)}
              />
            }
            label="Locked to edit"
            sx={{ width: "fit-content" }}
          />
        </RoleComponentGuard>
        <MaterialTooltip
          title="When disabled, this characteristic will never be sent to AI features, including autocomplete and document extraction."
          isShow
          variant="dark"
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={values.ai_complete}
                onChange={(e) => updatePlainData("ai_complete", e.target.checked)}
                disabled={values.is_locked_to_edit}
              />
            }
            label="Include in AI processing"
            sx={{ width: "fit-content" }}
          />
        </MaterialTooltip>
        <HorizontalNavBar
          value={selectedTab}
          tabs={[
            {
              icon: <InfoOutlinedIcon />,
              label: "Global Parameters",
              value: "global",
              onClick: () => handleTabChange("global")
            },
            {
              icon: <ListAltOutlinedIcon />,
              label: "List Options",
              value: "list",
              onClick: () => handleTabChange("list"),
              hidden: values?.data_type !== "list",
              ...((errors.list_options || Object.keys(optionsErrors).length) && { sx: { color: "red !important" } })
            }
          ]}
        />
        {selectedTab === "global" && <GlobalTab values={values} errors={errors} updatePlainData={updatePlainData} />}
        {selectedTab === "list" && (
          <ListTab
            values={values}
            setValues={setValues}
            subCategories={subCategories}
            optionsErrors={optionsErrors}
            setOptionsErrors={setOptionsErrors}
          />
        )}
      </Stack>
      <Footer onCancel={onCancel} onSubmit={() => handleSubmit(values)} isLoading={isLoading} />
    </ProgressOverlay>
  );
};

export const getAutocompleteValue = (value: string | null | undefined, options: { id: number; name: string }[]) => {
  return options.find((el) => el.name === value) || { id: 10000, name: "" };
};

const transformInitialStateToValues = (data?: CharacteristicsTableItem | null): Values => {
  const initialState: Values = {
    is_global: false,
    is_global_value: false,
    is_show: false,
    reduce_method: "",
    category_name: "General",
    subcategory_ids: [],
    is_multiple: false,
    postfix: "",
    numerify: "",
    data_type: "text",
    name: "",
    is_locked_to_edit: false,
    ai_complete: false,
    list_options: [],
    required_to_status_ids: null
  };

  if (!data) {
    return initialState;
  }

  // Pick characteristic values
  for (const key in data) {
    const value = data[key as keyof CharacteristicsTableItem];

    if (value === null) continue;
    if (key === "required_status_names") continue;

    if (key === "list_options") {
      if (Array.isArray(value)) {
        initialState.list_options = (value as string[]).map((option) => ({
          id: crypto.randomUUID(),
          value: option,
          subcategories: []
        }));

        continue;
      }

      if (typeof value === "object") {
        const allUniqueOptions = new Set<string>();

        Object.values(value as Record<number, string[]>).forEach((optionValues) => {
          optionValues.forEach((option) => {
            allUniqueOptions.add(option);
          });
        });

        initialState.list_options = Array.from(allUniqueOptions).map((option) => ({
          id: crypto.randomUUID(),
          value: option,
          subcategories: Object.entries(value as Record<number, string[]>).reduce((acc, [subCatId, optionValues]) => {
            if (optionValues.includes(option)) {
              return [...acc, parseInt(subCatId)];
            }
            return acc;
          }, [] as number[])
        }));
      }

      continue;
    }

    if (key === "required_to_status_ids") {
      initialState.required_to_status_ids =
        Array.isArray(value) && value.every((v): v is number => typeof v === "number") ? value : null;
      continue;
    }

    initialState[key as keyof CharacteristicsTableItem] = value as never;
  }

  return initialState;
};
